/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { 
  Button, 
  Container, 
  Paper, 
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
  Snackbar,
  Card,
  CardMedia
} from '@material-ui/core';
import { convertToRaw, ContentState, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { makeStyles } from '@material-ui/core/styles';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useParams, useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  editorContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    minHeight: '400px',
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  formField: {
    flex: '1 1 300px',
  },
  imageUpload: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: '2px dashed #ccc',
    borderRadius: '4px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  imagePreview: {
    marginTop: theme.spacing(2),
    maxWidth: '300px',
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
}));

const ArticlePost = ({ article, isEdit = false, onSuccess }) => {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isPosting, setIsPosting] = useState(false);
  const [postStatus, setPostStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [articleData, setArticleData] = useState({
    title: '',
    abstract: '',
    section: 'news',
    subsection: '',
    is_published: false,
    is_featured: false,
    des_facet: [],
    per_facet: [],
    image: null,
    imageCaption: '',
    existingImage: null
  });
  const [imagePreview, setImagePreview] = useState(null);

  const sections = [
    { value: 'news', label: 'News' },
    { value: 'opinion', label: 'Opinion' },
    { value: 'arts', label: 'Arts' },
    { value: 'sports', label: 'Sports' },
    { value: 'business', label: 'Business' },
    { value: 'technology', label: 'Technology' },
    { value: 'science', label: 'Science' },
    { value: 'health', label: 'Health' },
  ];

  useEffect(() => {
    if (isEdit && id) {
      fetchArticleData();
    } else if (article) {
      populateArticleData(article);
    }
  }, [id, isEdit, article]);

  const fetchArticleData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://app.zuddo.biz/main/api/admin/articles/${id}`, {
        headers: { 'x-access-token': token }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          populateArticleData(data.article);
        }
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      setPostStatus({ type: 'error', message: 'Failed to load article' });
    } finally {
      setIsLoading(false);
    }
  };

  const populateArticleData = (articleData) => {
    setArticleData({
      title: articleData.title || '',
      abstract: articleData.abstract || '',
      section: articleData.section || 'news',
      subsection: articleData.subsection || '',
      is_published: articleData.is_published || false,
      is_featured: articleData.is_featured || false,
      des_facet: articleData.des_facet ? articleData.des_facet.join(', ') : '',
      per_facet: articleData.per_facet ? articleData.per_facet.join(', ') : '',
      image: null,
      imageCaption: articleData.multimedia?.[0]?.caption || '',
      existingImage: articleData.multimedia?.[0]?.url || null
    });

    if (articleData.content) {
      const contentBlock = htmlToDraft(articleData.content);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
      }
    }

    if (articleData.multimedia?.[0]?.url) {
      setImagePreview(articleData.multimedia[0].url);
    }
  };

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setArticleData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setPostStatus({ type: 'error', message: 'Please select an image file' });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setPostStatus({ type: 'error', message: 'Image size must be less than 5MB' });
        return;
      }

      setArticleData(prev => ({ ...prev, image: file }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (publishStatus) => {
    if (!editorState || !articleData.title) {
      setPostStatus({ type: 'error', message: 'Title and content are required' });
      return;
    }
    
    setIsPosting(true);
    
    try {
      const contentState = editorState.getCurrentContent();
      const rawContent = convertToRaw(contentState);
      const articleHtml = draftToHtml(rawContent);
      
      const formData = new FormData();
      formData.append('title', articleData.title);
      formData.append('abstract', articleData.abstract);
      formData.append('content', articleHtml);
      formData.append('section', articleData.section);
      formData.append('subsection', articleData.subsection);
      formData.append('category_id', 1);
      formData.append('is_published', publishStatus);
      formData.append('is_featured', articleData.is_featured);
      
      if (articleData.image) {
        formData.append('image', articleData.image);
        formData.append('image_caption', articleData.imageCaption);
      }

      if (articleData.des_facet) {
        formData.append('des_facet', JSON.stringify(articleData.des_facet.split(',').map(f => f.trim())));
      }
      if (articleData.per_facet) {
        formData.append('per_facet', JSON.stringify(articleData.per_facet.split(',').map(f => f.trim())));
      }

      const token = localStorage.getItem('access_token');
      let method = 'POST';
      let url = 'https://jtimes.zuddo.biz/main/articles';

      if (isEdit && (id || article?.id)) {
        const articleId = id || article?.id;
        url = `https://jtimes.zuddo.biz/main/api/admin/articles/${articleId}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method: method,
        headers: { 'x-access-token': token }, 
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setPostStatus({ 
          type: 'success', 
          message: isEdit 
            ? `Article updated successfully!` 
            : `Article ${publishStatus ? 'published' : 'saved as draft'} successfully!` 
        });
        
        if (onSuccess) {
          onSuccess(result.article);
        } else if (!isEdit) {
          setArticleData({
            title: '',
            abstract: '',
            section: 'news',
            subsection: '',
            is_published: false,
            is_featured: false,
            des_facet: [],
            per_facet: [],
            image: null,
            imageCaption: '',
            existingImage: null
          });
          setEditorState(EditorState.createEmpty());
          setImagePreview(null);
        }
        if (!onSuccess) {
          setTimeout(() => {
            navigate('/admin/dashboard');
          }, 2000);
        }
      } else {
        setPostStatus({ type: 'error', message: result.error || result.message || 'Failed to save article' });
      }
    } catch (error) {
      console.error('Error posting article:', error);
      setPostStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsPosting(false);
    }
  };

  const handleSaveDraft = () => {
    handleSubmit(false);
  };

  const handlePublishArticle = () => {
    handleSubmit(true);
  };

  const handleCloseSnackbar = () => {
    setPostStatus(null);
  };

  if (isLoading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        <Paper className={classes.paper} elevation={3}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {isEdit ? 'Edit Article' : 'Article Information'}
          </Typography>

          <div className={classes.formRow}>
            <TextField
              className={classes.formField}
              label="Article Title"
              name="title"
              value={articleData.title}
              onChange={handleInputChange}
              required
              fullWidth
              variant="outlined"
            />
            
            <FormControl className={classes.formField} variant="outlined">
              <InputLabel>Section</InputLabel>
              <Select
                name="section"
                value={articleData.section}
                onChange={handleInputChange}
                label="Section"
              >
                {sections.map((section) => (
                  <MenuItem key={section.value} value={section.value}>
                    {section.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={classes.formRow}>
            <TextField
              className={classes.formField}
              label="Abstract/Summary"
              name="abstract"
              value={articleData.abstract}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
            />
            
            <TextField
              className={classes.formField}
              label="Subsection"
              name="subsection"
              value={articleData.subsection}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </div>

          <div className={classes.formRow}>
            <TextField
              className={classes.formField}
              label="Descriptive Facets (comma-separated)"
              name="des_facet"
              value={articleData.des_facet}
              onChange={handleInputChange}
              placeholder="Politics, Technology, Economy"
              fullWidth
              variant="outlined"
            />
            
            <TextField
              className={classes.formField}
              label="People Facets (comma-separated)"
              name="per_facet"
              value={articleData.per_facet}
              onChange={handleInputChange}
              placeholder="John Doe, Jane Smith"
              fullWidth
              variant="outlined"
            />
          </div>

          <Typography variant="h6" className={classes.sectionTitle}>
            Featured Image
          </Typography>

          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="featured-image-upload"
            type="file"
            onChange={handleImageUpload}
          />
          <label htmlFor="featured-image-upload">
            <div className={classes.imageUpload}>
              <Typography variant="body1">
                {articleData.image || articleData.existingImage 
                  ? 'Click to change image' 
                  : 'Click to upload featured image'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Max 5MB • JPG, PNG, GIF, WEBP
              </Typography>
            </div>
          </label>

          {imagePreview && (
            <Card className={classes.imagePreview}>
              <CardMedia
                component="img"
                alt="Featured image preview"
                height="200"
                image={imagePreview}
              />
            </Card>
          )}

          {(articleData.image || articleData.existingImage) && (
            <TextField
              fullWidth
              label="Image Caption"
              name="imageCaption"
              value={articleData.imageCaption}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
            />
          )}

          <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: '2rem' }}>
            Article Content
          </Typography>

          <div className={classes.editorContainer}>
            <Editor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
              }}
            />
          </div>

          <Typography variant="h6" className={classes.sectionTitle}>
            Publishing Options
          </Typography>

          <div className={classes.formRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={articleData.is_featured}
                  onChange={handleInputChange}
                  name="is_featured"
                  color="primary"
                />
              }
              label="Feature this article"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={articleData.is_published}
                  onChange={handleInputChange}
                  name="is_published"
                  color="primary"
                />
              }
              label="Publish immediately"
            />
          </div>

          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSaveDraft}
              disabled={isPosting}
              startIcon={isPosting ? <CircularProgress size={20} /> : null}
            >
              {isPosting ? 'Saving...' : isEdit ? 'Update Draft' : 'Save as Draft'}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handlePublishArticle}
              disabled={isPosting}
              startIcon={isPosting ? <CircularProgress size={20} /> : null}
            >
              {isPosting ? 'Publishing...' : isEdit ? 'Update Article' : 'Publish Article'}
            </Button>
          </div>
        </Paper>

        <Snackbar
          open={!!postStatus}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Typography
            onClose={handleCloseSnackbar} 
            severity={postStatus?.type} 
            variant="filled"
          >
            {postStatus?.message}
          </Typography>
        </Snackbar>
      </Container>
    </div>
  );
};

export default ArticlePost;